import { animate, keyframes, state as animationState, style, transition, trigger } from '@angular/animations';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { faCommentCheck } from '@fortawesome/pro-duotone-svg-icons';
import { faPlay, faRepeat, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { take } from 'rxjs';

import { Media } from '@eiq/files/models';

import { AiChatMessage, AiChatMessageRole, AiChatState, AiChatStatus, IFollowupQuestionRequest, Question } from '@empathy/survey/base';
import { createTurnId } from '@empathy/survey/base/turn-id';

import { AiChatAudioAnswer } from '../../../models/ai-chat-audio-answer.interface';
import { AiChatMessageView } from '../../../models/ai-chat-message-view.interface';
import { SurveyViewerService } from '../../../services/survey-viewer.service';

const AUDIO_REFERENCE_PREFIX = 'audio_';

@Component({
  selector: 'empathy-ai-chat-question-view',
  templateUrl: 'ai-chat-question-view.component.html',
  styleUrls: ['./ai-chat-question-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('inOutAnimation', [
      animationState('in', style({ opacity: 1 })),
      transition(':enter', [
        animate(
          150,
          keyframes([
            style({ opacity: 0, offset: 0 }),
            style({ opacity: 0.25, offset: 0.25 }),
            style({ opacity: 0.5, offset: 0.5 }),
            style({ opacity: 0.75, offset: 0.75 }),
            style({ opacity: 1, offset: 1 })
          ])
        )
      ])
    ])
  ]
})
export class AIChatQuestionViewComponent implements AfterViewChecked {
  question = input<Question>(null);
  questionCtrl = input<AbstractControl>(null);
  chatState = input<AiChatState>();

  httpError = output<void>();

  readonly completed$ = computed(() => this.serverState$()?.status === AiChatStatus.Completed);
  readonly isPlaceholder$ = computed(() => this.question()?.aiChatPlaceholderInd === true);
  readonly initialQuestion$ = computed(() => {
    const state = this.serverState$();
    return state?.messages.length === 0 ? (state.pendingQuestion ?? this.question()?.startingQuestion) : this.question()?.startingQuestion;
  });
  readonly canAnswer$ = computed(() => !this.isPlaceholder$() && this.serverState$()?.status === AiChatStatus.Active && !this.waiting$());
  readonly messages$ = computed(() => this.serverState$()?.messages ?? []);
  readonly messageViews$ = computed<AiChatMessageView[]>(() =>
    this.messages$().map((message, index) => this.toCommittedMessageView(message, `${this.question().id}-${index}`))
  );
  readonly pendingAnswerView$ = computed<AiChatMessageView | null>(() => {
    const answer = this.pendingAnswer$();
    return answer === null ? null : this.toPendingMessageView(answer, `${this.question().id}-pending`);
  });
  readonly stimulusRaw$ = computed<Media | null>(() => this.question()?.stimulus?.[0] ?? null);

  processingAudio$ = signal(false);
  retryableError$ = signal(false);
  sendFailed$ = signal(false);
  waiting$ = signal(false);

  readonly completedMessage: string;
  readonly faCommentCheck = faCommentCheck;
  readonly faPlay = faPlay;
  readonly faRepeat = faRepeat;
  readonly faSparkles = faSparkles;

  private readonly serverState$ = signal<AiChatState | null>(null);
  private readonly pendingAnswer$ = signal<string | null>(null);
  private readonly audioByMediaId$ = signal<Record<string, Blob>>({});

  private pendingTurnId: string | null = null;
  private readonly loadingMediaIds = new Set<string>();

  private readonly surveyViewerService = inject(SurveyViewerService);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.completedMessage = this.translate.instant('system.aiChat.continue');

    effect(
      () => {
        const state = this.chatState();
        if (state) {
          this.serverState$.set(state);
          this.clearPendingTurn();
        }
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const control = this.questionCtrl();
      if (!control) {
        return;
      }

      control.setValidators(() => this.mapValidationErrors());
      control.updateValueAndValidity();
    });

    effect(
      () => {
        this.messages$()
          .filter((message) => message.role === AiChatMessageRole.Participant && message.mediaId !== undefined)
          .forEach((message) => this.loadAudio(message.mediaId));
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  onAnswer(answerText: string): void {
    this.pendingTurnId = createTurnId();
    this.pendingAnswer$.set(answerText);
    this.requestFollowUp();
  }

  onAnswerAudio(audioAnswer: AiChatAudioAnswer): void {
    this.pendingTurnId = audioAnswer.turnId;
    this.pendingAnswer$.set(audioAnswer.audioReference);
    const mediaId = this.parseAudioReference(audioAnswer.audioReference);
    this.audioByMediaId$.update((audioByMediaId) => ({ ...audioByMediaId, [mediaId]: audioAnswer.audio }));
    this.requestFollowUp();
  }

  onResend(): void {
    this.requestFollowUp();
  }

  onProcessing(processing: boolean): void {
    this.processingAudio$.set(processing);
    this.syncValidity();
  }

  private requestFollowUp(): void {
    const answerText = this.pendingAnswer$();
    if (!this.pendingTurnId || answerText === null) {
      return;
    }

    const request: IFollowupQuestionRequest = {
      answerText,
      questionId: this.question().id,
      turnId: this.pendingTurnId
    };

    this.waiting$.set(true);
    this.sendFailed$.set(false);
    this.retryableError$.set(false);
    this.syncValidity();

    this.surveyViewerService.aiFollowupCall(request).subscribe({
      error: (error: HttpErrorResponse) => this.processRequestError(error),
      next: (state) => {
        this.serverState$.set(state);
        this.clearPendingTurn();
      }
    });
  }

  private processRequestError(error: HttpErrorResponse): void {
    this.waiting$.set(false);
    this.sendFailed$.set(true);
    this.retryableError$.set(this.isRetryable(error));
    this.syncValidity();

    if (error.status === HttpStatusCode.Unauthorized) {
      this.httpError.emit();
    }
  }

  private isRetryable(error: HttpErrorResponse): boolean {
    const response = error.error as { retryable?: boolean } | null;
    if (typeof response?.retryable === 'boolean') {
      return response.retryable;
    }

    return error.status === 0 || error.status === HttpStatusCode.RequestTimeout || error.status === HttpStatusCode.TooManyRequests || error.status >= 500;
  }

  private loadAudio(mediaId: string): void {
    if (this.audioByMediaId$()[mediaId] || this.loadingMediaIds.has(mediaId)) {
      return;
    }

    this.loadingMediaIds.add(mediaId);
    this.surveyViewerService
      .downloadAiChatAudio(this.question().id, mediaId)
      .pipe(take(1))
      .subscribe({
        error: (error: HttpErrorResponse) => {
          this.loadingMediaIds.delete(mediaId);
          if (error.status === HttpStatusCode.Unauthorized) {
            this.httpError.emit();
          }
        },
        next: (audio) => {
          this.loadingMediaIds.delete(mediaId);
          this.audioByMediaId$.update((audioByMediaId) => ({ ...audioByMediaId, [mediaId]: audio }));
        }
      });
  }

  private clearPendingTurn(): void {
    this.pendingTurnId = null;
    this.pendingAnswer$.set(null);
    this.waiting$.set(false);
    this.sendFailed$.set(false);
    this.retryableError$.set(false);
    this.syncValidity();
  }

  private mapValidationErrors(): ValidationErrors | null {
    if (this.isPlaceholder$()) {
      return this.question()?.requiredInd ? { required: true } : null;
    }

    if (this.waiting$() || this.processingAudio$()) {
      return { aiChatPending: true };
    }

    if (this.sendFailed$()) {
      return { aiChatError: true };
    }

    return this.question()?.requiredInd && !this.completed$() ? { required: true } : null;
  }

  private syncValidity(): void {
    this.questionCtrl()?.updateValueAndValidity();
  }

  private parseAudioReference(audioReference: string): string {
    return audioReference.substring(AUDIO_REFERENCE_PREFIX.length);
  }

  private toCommittedMessageView(message: AiChatMessage, audioPlayerId: string): AiChatMessageView {
    const isAssistant = message.role === AiChatMessageRole.Assistant;
    const isAudio = !isAssistant && message.mediaId !== undefined;
    return {
      audio: isAudio ? (this.audioByMediaId$()[message.mediaId] ?? null) : null,
      audioPlayerId,
      content: message.content,
      isAssistant,
      isAudio
    };
  }

  private toPendingMessageView(content: string, audioPlayerId: string): AiChatMessageView {
    const isAudio = content.startsWith(AUDIO_REFERENCE_PREFIX);
    const mediaId = isAudio ? this.parseAudioReference(content) : undefined;
    return {
      audio: mediaId === undefined ? null : (this.audioByMediaId$()[mediaId] ?? null),
      audioPlayerId,
      content,
      isAssistant: false,
      isAudio
    };
  }

  private scrollToBottom(): void {
    const pageContainer = document.getElementById('page-view__container');
    if (pageContainer) {
      pageContainer.scrollTop = pageContainer.scrollHeight;
    }
  }
}
